import { useEffect, useState } from 'react'
import { errorMessage, get } from '../api'
import { useDebounced } from '../hooks'
import { daysLabel } from '../format'
import { Alert, Button, Input } from './ui'

// "Find your restaurant on Google": a search box backed by the API's Places proxy. Picking a
// result returns the details (and suggested service periods) for the caller to prefill with;
// nothing is saved until the owner confirms the form.

export interface PlaceSummary {
  placeId: string
  name: string
  address: string | null
}

export interface PlaceDetails extends PlaceSummary {
  phone: string | null
  website: string | null
  mapsUrl: string | null
  location: { lat: number; lng: number } | null
  utcOffsetMinutes: number | null
}

export interface SuggestedPeriod {
  name: string
  daysOfWeek: number[]
  starts: string
  ends: string
}

export function PlaceFinder({ onPick, region }: { onPick: (place: PlaceDetails, suggestedPeriods: SuggestedPeriod[]) => void; region?: string }) {
  const [q, setQ] = useState('')
  const query = useDebounced(q.trim(), 350)
  const [results, setResults] = useState<PlaceSummary[]>([])
  const [searching, setSearching] = useState(false)
  const [picking, setPicking] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (query.length < 2) {
      setResults([])
      return
    }
    let cancelled = false
    setSearching(true)
    setError(null)
    const params = new URLSearchParams({ q: query, lang: navigator.language.slice(0, 2) })
    if (region) params.set('region', region)
    get<{ results: PlaceSummary[] }>(`/v1/admin/places/search?${params}`)
      .then((r) => !cancelled && setResults(r.results))
      .catch((err) => !cancelled && setError(errorMessage(err)))
      .finally(() => !cancelled && setSearching(false))
    return () => {
      cancelled = true
    }
  }, [query, region])

  const pick = async (summary: PlaceSummary) => {
    setPicking(summary.placeId)
    setError(null)
    try {
      const r = await get<{ place: PlaceDetails; suggestedPeriods: SuggestedPeriod[] }>(`/v1/admin/places/${encodeURIComponent(summary.placeId)}?lang=${navigator.language.slice(0, 2)}`)
      onPick(r.place, r.suggestedPeriods)
      setResults([])
      setQ('')
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setPicking(null)
    }
  }

  return (
    <div className="stack" style={{ gap: 8 }}>
      <div className="search">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
        <Input placeholder="Find your restaurant on Google: name and town" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Find your restaurant on Google" autoComplete="off" />
      </div>
      {searching ? <p className="hint">Searching…</p> : null}
      {error ? <Alert>{error}</Alert> : null}
      {results.length ? (
        <ul className="list-plain card" style={{ padding: '0 12px' }}>
          {results.map((r) => (
            <li key={r.placeId}>
              <div>
                <div style={{ fontWeight: 600 }}>{r.name}</div>
                <div className="muted small">{r.address}</div>
              </div>
              <Button size="sm" busy={picking === r.placeId} onClick={() => void pick(r)}>
                Use
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
      {query.length >= 2 && !searching && !results.length && !error ? <p className="hint">Nothing found. Try adding the town, or carry on by hand.</p> : null}
    </div>
  )
}

/** A readable preview of the hours Google reports, for the owner to accept or ignore. */
export function PeriodsPreview({ periods }: { periods: SuggestedPeriod[] }) {
  return (
    <ul className="list-plain small">
      {periods.map((p, i) => (
        <li key={i}>
          <span>
            {p.name} · {p.starts}–{p.ends}
          </span>
          <span className="muted">{daysLabel(p.daysOfWeek)}</span>
        </li>
      ))}
    </ul>
  )
}

